import { useState, type FormEvent } from 'react';
import { ApiError, api } from '../lib/api';
import { Button } from './ui/Button';
import {
  FIELD_LABEL_CLASS,
  FIELD_INPUT_CLASS,
  FIELD_HINT_CLASS,
} from './ui/form-field';

const MIN_LENGTH = 10;

/**
 * Passwort-Ändern-Formular. Zwei Einsatzorte:
 *  - `forced` (App.tsx): nach Login mit `mustChangePassword` — blockt App-Zugriff
 *    bis geändert; `onSuccess` lädt die App neu (frische /me-Row ohne Flag).
 *  - freiwillig (ProfilePage): User ändert sein Passwort selbst.
 *
 * Backend: POST /api/auth/change-password (verifiziert currentPassword,
 * Policy min 10, falsches current → 400). Client-seitige Vor-Validierung
 * (min 10, confirm-match) spart einen Roundtrip, ist aber NICHT die
 * Sicherheitsgrenze — die liegt im Server-Schema.
 */
export function ChangePasswordForm({
  forced = false,
  onSuccess,
}: {
  forced?: boolean;
  onSuccess?: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (next.length < MIN_LENGTH) {
      setError(`Neues Passwort muss mind. ${MIN_LENGTH} Zeichen haben.`);
      return;
    }
    if (next !== confirm) {
      setError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }
    setStatus('loading');
    try {
      await api('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      setStatus('done');
      onSuccess?.();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Passwort-Änderung fehlgeschlagen. Bitte erneut versuchen.',
      );
      setStatus('idle');
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label htmlFor="cp-current" className={FIELD_LABEL_CLASS}>
          Aktuelles Passwort
        </label>
        <input
          id="cp-current"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
          autoComplete="current-password"
          className={FIELD_INPUT_CLASS}
        />
      </div>
      <div>
        <label htmlFor="cp-next" className={FIELD_LABEL_CLASS}>
          Neues Passwort
        </label>
        <input
          id="cp-next"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
          minLength={MIN_LENGTH}
          autoComplete="new-password"
          className={FIELD_INPUT_CLASS}
        />
        <p className={FIELD_HINT_CLASS}>Mind. {MIN_LENGTH} Zeichen.</p>
      </div>
      <div>
        <label htmlFor="cp-confirm" className={FIELD_LABEL_CLASS}>
          Neues Passwort bestätigen
        </label>
        <input
          id="cp-confirm"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          autoComplete="new-password"
          className={FIELD_INPUT_CLASS}
        />
      </div>

      {error && (
        <div
          className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          {error}
        </div>
      )}
      {status === 'done' && !forced && (
        <div
          className="rounded-md bg-brand-green-100 border border-brand-green-200 px-3 py-2 text-sm text-brand-green-700"
          role="status"
        >
          Passwort geändert.
        </div>
      )}

      <Button type="submit" variant="primary" fullWidth={forced} loading={status === 'loading'}>
        {status === 'loading' ? 'Speichert…' : 'Passwort ändern'}
      </Button>
    </form>
  );
}
